import React from 'react';
import Table from '../../../core/form/Table';
import PropTypes from 'prop-types';
import Button from '../../../core/form/Button';
import Async from '../../../core/layout/Async';
import { browserHistory } from 'react-router';
import moment from 'moment';
import { upperFirst, startCase, last } from 'lodash';
import Row from '../../../core/layout/Row';
import PermissionValidator from '../../../core/layout/PermissionValidator';
import { realCurrencyFormatter, dollarCurrencyFormatter, BRAZIL } from '../../../core/consts';
import Separator from '../../../core/layout/Separator';
import { Modal, Tooltip } from 'antd';
import Icon from '../../../core/layout/Icon';
import get from 'lodash/get';
import TextInput from '../../../core/form/TextInput';
import NotificationService from '../../../core/services/NotificationService';

const BillList = (props) => {
  const handleCopyToClipboard = React.useCallback(async (text) => {
    await navigator.clipboard.writeText(text);
    NotificationService.addNotification(`Link successfully copied to clipboard`, 'success');
  }, []);

  return (
    <Async fetching={props.fetching}>
      <Row>
        <Table
          lazy={true}
          columns={[
            {
              label: 'Bill ID',
              path: 'billId',
              sort: true,
              defaultSortOrder: 'ascend',
              sortOrder: props.sortedInfo.columnKey === 'billId' && props.sortedInfo.order,
            },
            {
              label: 'Document Number',
              path: 'contract.documentNumber',
              width: '150px',
              sort: true,
              sortOrder: props.sortedInfo.columnKey === 'contract.documentNumber' && props.sortedInfo.order,
            },
            {
              label: 'Social Reason',
              path: 'contract.socialReason',
              sort: true,
              sortOrder: props.sortedInfo.columnKey === 'contract.socialReason' && props.sortedInfo.order,
            },
            {
              label: 'Created Date',
              path: 'createdAt',
              sort: true,
              render: (value) => moment(value).utc().format('DD/MM/YYYY'),
              sortOrder: props.sortedInfo.columnKey === 'createdAt' && props.sortedInfo.order,
            },
            {
              label: 'Due Date',
              path: 'dueDate',
              sort: true,
              render: (value) => moment(value).utc().format('DD/MM/YYYY'),
              sortOrder: props.sortedInfo.columnKey === 'dueDate' && props.sortedInfo.order,
            },
            {
              label: 'Total Price',
              path: 'total',
              sort: true,
              sortOrder: props.sortedInfo.columnKey === 'total' && props.sortedInfo.order,
              render: (value, row) =>
                BRAZIL.includes(row.contract.country)
                  ? realCurrencyFormatter.format(value)
                  : dollarCurrencyFormatter.format(value),
            },
            {
              label: 'Status',
              path: 'status',
              sort: true,
              sortOrder: props.sortedInfo.columnKey === 'status' && props.sortedInfo.order,
              render: (value, row) => (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    gap: 10,
                  }}
                  key={row.id}
                >
                  {upperFirst(startCase(value.toLowerCase()))}
                  {row.status === 'PROCESSING_PAYMENT' && !!row.paymentGatewayErrors.length && (
                    <Tooltip
                      title={(() => {
                        const lastError = last(row.paymentGatewayErrors);
                        return `LR: ${lastError?.lr}. ${lastError?.description} ${lastError?.action}`;
                      })()}
                    >
                      <div>
                        <Icon name="info-circle" />
                      </div>
                    </Tooltip>
                  )}
                </div>
              ),
            },
            {
              label: 'Payment Date',
              path: 'paidAt',
              sort: true,
              render: (value) => (value ? moment(value).utc().format('DD/MM/YYYY') : null),
              sortOrder: props.sortedInfo.columnKey === 'paidAt' && props.sortedInfo.order,
            },
            {
              label: 'Payment Value',
              path: 'paidValue',
              sort: true,
              render: (value, row) =>
                value
                  ? BRAZIL.includes(row.contract.country)
                    ? realCurrencyFormatter.format(value)
                    : dollarCurrencyFormatter.format(value)
                  : null,
              sortOrder: props.sortedInfo.columnKey === 'paidValue' && props.sortedInfo.order,
            },
            {
              label: 'Actions',
              width: '120px',
              render: (cell, row) => (
                <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                  <Button
                    icon="eye"
                    tooltip="Details"
                    onClick={() =>
                      browserHistory.push({
                        pathname: `/billing/${row.id}/details`,
                        state: { from: { text: 'Billing', pathname: '/billing' } },
                      })
                    }
                  />
                  {!!props.onDelete && <Button icon="delete" onClick={() => props.onDelete(row)} />}
                  {row.cancellationReason && row.status === 'CANCELED' && (
                    <>
                      <Button
                        icon="exception"
                        tooltip="Cancellation reason"
                        onClick={() => props.onAction('cancel', row)}
                      />
                      <Button icon="folder" tooltip="Reopen bill" onClick={() => props.onAction('open', row)} />
                    </>
                  )}
                  <PermissionValidator allowedFor={['ADMIN', 'SUPPORT']}>
                    {row.status === 'REVIEW' && (
                      <>
                        <Button
                          icon="edit"
                          onClick={() =>
                            browserHistory.push({
                              pathname: `/billing/${row.id}`,
                              state: { from: { text: 'Billing', pathname: '/billing' } },
                            })
                          }
                        />
                        <Button icon="folder" tooltip="Reopen bill" onClick={() => props.onAction('open', row)} />
                        <Button
                          icon="gift"
                          tooltip="Set bill as no charge"
                          onClick={() => props.onAction('gift', row)}
                        />
                      </>
                    )}
                    {row.status === 'OPEN' && (
                      <>
                        <Button
                          icon="edit"
                          onClick={() =>
                            browserHistory.push({
                              pathname: `/billing/${row.id}`,
                              state: { from: { text: 'Billing', pathname: '/billing' } },
                            })
                          }
                        />
                        <Button icon="mail" tooltip="Send to Client" onClick={() => props.onAction('send', row)} />
                      </>
                    )}
                    {row.status === 'SENT_TO_CLIENT' && (
                      <>
                        <Button
                          icon="credit-card"
                          tooltip="Set bill as payed"
                          onClick={() => props.onAction('pay', row)}
                        />
                      </>
                    )}
                  </PermissionValidator>
                  {row.status === 'PAID' &&
                    row.paymentGatewayInvoicePaymentMethod &&
                    (row.paymentGatewayInvoicePaymentMethod.includes('PIX') ||
                      row.paymentGatewayInvoicePaymentMethod.includes('CREDIT_CARD')) && (
                      <Button icon="close-circle" tooltip="Cancel bill" onClick={() => props.onAction('cancel', row)} />
                    )}
                  {['SENT_TO_CLIENT', 'PROCESSING_PAYMENT', 'OPEN'].includes(row.status) && (
                    <Button icon="close-circle" tooltip="Cancel bill" onClick={() => props.onAction('cancel', row)} />
                  )}
                  {['SENT_TO_CLIENT', 'PAID'].includes(row.status) && row.billUrl && (
                    <Button
                      icon="file-pdf"
                      tooltip="Download bill"
                      onClick={() => props.onAction('download', row.billUrl)}
                    />
                  )}
                  {['OPEN', 'SENT_TO_CLIENT'].includes(row.status) && row.bankBillUrl && (
                    <Button
                      icon="barcode"
                      tooltip="Download bank slip"
                      onClick={() => props.onAction('download', row.bankBillUrl)}
                    />
                  )}
                  {['SENT_TO_CLIENT'].includes(row.status) && row.paymentGatewayInvoiceUrl && (
                    <Button
                      icon="dollar"
                      tooltip="Show payment link"
                      onClick={() => {
                        Modal.info({
                          title: 'Payment links',
                          okText: 'Close',
                          width: '50%',
                          content: (
                            <>
                              {/*<a href={row.paymentGatewayInvoiceUrl} target="_blank" rel="noopener noreferrer">*/}
                              {/*  {row.paymentGatewayInvoiceUrl}*/}
                              {/*</a>*/}
                              <div>
                                <TextInput
                                  addonBefore={
                                    <Button
                                      icon="copy"
                                      size="sm"
                                      tooltip="Copy payment link"
                                      onClick={async () => {
                                        await handleCopyToClipboard(row.paymentGatewayInvoiceUrl);
                                      }}
                                    />
                                  }
                                  disabled
                                  label="Payment link"
                                  value={get(row, 'paymentGatewayInvoiceUrl', '')}
                                />
                                {row.paymentGatewayInvoiceExternalUrl && (
                                  <TextInput
                                    addonBefore={
                                      <Button
                                        icon="copy"
                                        size="sm"
                                        tooltip="Copy external payment link"
                                        onClick={async () => {
                                          await handleCopyToClipboard(row.paymentGatewayInvoiceExternalUrl);
                                        }}
                                      />
                                    }
                                    disabled
                                    label="Payment Gateway External link"
                                    value={get(row, 'paymentGatewayInvoiceExternalUrl', '')}
                                  />
                                )}
                              </div>
                            </>
                          ),
                        });
                      }}
                    />
                  )}
                </div>
              ),
            },
          ]}
          rows={props.bills}
          onSelectRows={
            ['ADMIN', 'SUPPORT'].includes(localStorage.role)
              ? (selectedBills) => props.onAction('select', selectedBills)
              : null
          }
          selectedRows={props.selectedBills}
          rowKey="id"
          pagination={props.pagination}
          onChange={props.onChange}
        />
      </Row>
      <PermissionValidator allowedFor={['ADMIN', 'SUPPORT']}>
        <Separator />
        <Row>
          <div style={{ textAlign: 'right' }}>
            <Button
              onClick={() => props.onAction('send')}
              disabled={
                !props.selectedBills
                  .map((selectedBillId) => props.bills.find((bill) => bill.id === selectedBillId))
                  .some((bill) => bill.status === 'OPEN')
              }
              icon="mail"
              label="Send to Client"
            />{' '}
          </div>
        </Row>
      </PermissionValidator>
    </Async>
  );
};

BillList.propTypes = {
  bills: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      billId: PropTypes.string.isRequired,
      contract: PropTypes.shape({
        documentNumber: PropTypes.string.isRequired,
        socialReason: PropTypes.string.isRequired,
      }).isRequired,
      dueDate: PropTypes.string.isRequired,
      total: PropTypes.number.isRequired,
      status: PropTypes.oneOf(['OPEN', 'SENT_TO_CLIENT', 'PAID', 'NO_CHARGE', 'REVIEW']).isRequired,
      paidAt: PropTypes.string,
      paidValue: PropTypes.number,
    })
  ).isRequired,
  pagination: PropTypes.shape({
    current: PropTypes.number,
    total: PropTypes.number,
    pageSize: PropTypes.number,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  fetching: PropTypes.bool.isRequired,
  selectedBills: PropTypes.arrayOf(PropTypes.string),
  onAction: PropTypes.func.isRequired,
  sortedInfo: PropTypes.object,
};

BillList.defaultProps = {
  selectedBills: [],
  sortedInfo: {},
};

export default BillList;
